import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { can, DISABLED_BTN_CLS, permissionTitle } from "../lib/permissions";
import { MODE_NAMES } from "../components/Badges";
import { Plus, Edit2, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { mode_name: "Standard", description: "", gate_sequence: [] };

export default function ControlModes() {
    const { user } = useAuth();
    const canManage = can(user, "manageControlModes");
    const [modes, setModes] = useState([]);
    const [gates, setGates] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [error, setError] = useState("");

    const refresh = async () => {
        try {
            const [m, g] = await Promise.all([
                api.get("/control-modes").then((r) => r.data),
                api.get("/gate-templates").then((r) => r.data),
            ]);
            setModes(m);
            setGates(g);
        } catch (err) {
            const status = err?.response?.status;
            if (status !== 401) {
                console.warn("[ControlModes] refresh failed:", status, err?.message);
                toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Failed to load control modes");
            }
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const openCreate = () => { setForm(EMPTY); setEditing({}); setError(""); };
    const openEdit = (m) => {
        setForm({ mode_name: m.mode_name, description: m.description, gate_sequence: m.gate_sequence });
        setEditing(m);
        setError("");
    };
    const close = () => setEditing(null);

    const toggleGate = (id) => {
        const has = form.gate_sequence.includes(id);
        const next = has ? form.gate_sequence.filter((g) => g !== id) : [...form.gate_sequence, id].sort((a, b) => a - b);
        setForm({ ...form, gate_sequence: next });
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            if (editing && editing.id) {
                await api.patch(`/control-modes/${editing.id}`, form);
                toast.success("Control mode updated");
            } else {
                await api.post("/control-modes", form);
                toast.success("Control mode created");
            }
            close();
            refresh();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/control-modes/${deleteTarget.id}`);
            toast.success("Control mode deleted");
            setDeleteTarget(null);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-6" data-testid="control-modes-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="overline">Master Records / Control Modes</div>
                    <h1 className="heading-display text-4xl mt-1">Control Modes</h1>
                    <p className="text-snow-dim text-sm mt-2 max-w-2xl">
                        Review tracks. Each mode defines an ordered sequence of gates a product must pass.
                    </p>
                </div>
                <button
                    data-testid="create-mode-btn"
                    onClick={openCreate}
                    disabled={!canManage}
                    title={permissionTitle(user, "manageControlModes")}
                    className={`bg-signal text-jet hover:bg-signal-hover px-5 py-2 transition-colors font-bold uppercase tracking-[0.2em] text-xs flex items-center gap-2 ${!canManage ? DISABLED_BTN_CLS : ""}`}
                >
                    <Plus className="w-3 h-3" /> New Mode
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {modes.map((m) => (
                    <div key={m.id} data-testid={`mode-card-${m.id}`} className="border border-hairline bg-jet-elev1 p-5 flex flex-col">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="overline">Mode</div>
                                <div className="heading-display text-xl mt-1">{m.mode_name}</div>
                            </div>
                            <div className="flex gap-1">
                                <button
                                    data-testid={`edit-mode-${m.id}`}
                                    onClick={() => canManage && openEdit(m)}
                                    disabled={!canManage}
                                    title={permissionTitle(user, "manageControlModes")}
                                    className={`p-1.5 border transition-colors ${!canManage ? DISABLED_BTN_CLS : "border-hairline text-snow-dim hover:border-signal hover:text-signal"}`}
                                >
                                    <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    data-testid={`delete-mode-${m.id}`}
                                    onClick={() => canManage && setDeleteTarget(m)}
                                    disabled={!canManage}
                                    title={permissionTitle(user, "manageControlModes")}
                                    className={`p-1.5 border transition-colors ${!canManage ? DISABLED_BTN_CLS : "border-hairline text-snow-dim hover:border-red-500 hover:text-red-400"}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                        <p className="text-xs text-snow-dim mt-3 font-mono leading-relaxed flex-1">{m.description}</p>
                        <div className="mt-4 pt-4 border-t border-hairline">
                            <div className="overline mb-2">Gate Sequence ({m.gate_sequence.length})</div>
                            <div className="flex flex-wrap gap-1">
                                {m.gate_sequence.map((id) => (
                                    <span key={id} className="border border-signal/40 text-signal bg-signal/5 px-2 py-0.5 text-[11px] font-mono tabular-nums">
                                        G{String(id).padStart(2, "0")}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing !== null && (
                <Modal title={editing.id ? "Edit Control Mode" : "New Control Mode"} onClose={close}>
                    <form onSubmit={submit} className="space-y-4" data-testid="mode-form">
                        <Field label="Mode Name">
                            <select
                                data-testid="mode-name-select"
                                value={form.mode_name}
                                onChange={(e) => setForm({ ...form, mode_name: e.target.value })}
                                className="cerb-input"
                            >
                                {MODE_NAMES.map((n) => <option key={n}>{n}</option>)}
                            </select>
                        </Field>
                        <Field label="Description">
                            <textarea
                                data-testid="mode-description-input"
                                rows={3}
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                className="cerb-input"
                            />
                        </Field>
                        <Field label={`Gate Sequence (${form.gate_sequence.length} selected)`}>
                            <div className="grid grid-cols-6 gap-2" data-testid="mode-gate-grid">
                                {gates.map((g) => {
                                    const selected = form.gate_sequence.includes(g.gate_id);
                                    return (
                                        <button
                                            key={g.id}
                                            type="button"
                                            data-testid={`gate-pill-${g.gate_id}`}
                                            onClick={() => toggleGate(g.gate_id)}
                                            className={`border px-2 py-2 text-xs font-mono transition-colors ${
                                                selected
                                                    ? "border-signal text-signal bg-signal/10"
                                                    : "border-hairline text-snow-dim hover:border-snow"
                                            }`}
                                            title={g.gate_name}
                                        >
                                            G{String(g.gate_id).padStart(2, "0")}
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>
                        {error && <div className="text-red-400 text-xs font-mono">{error}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={close} className="cerb-btn-secondary" data-testid="mode-cancel-btn">Cancel</button>
                            <button type="submit" disabled={saving} className="cerb-btn-primary" data-testid="mode-save-btn">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {deleteTarget && (
                <Modal title="Delete Control Mode" onClose={() => setDeleteTarget(null)}>
                    <div className="space-y-4">
                        <div className="text-sm">Permanently delete <span className="text-signal font-bold">{deleteTarget.mode_name}</span>?</div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => setDeleteTarget(null)} className="cerb-btn-secondary">Cancel</button>
                            <button onClick={confirmDelete} className="bg-red-500 text-snow hover:bg-red-600 px-6 py-2 font-bold uppercase tracking-[0.2em] text-xs" data-testid="mode-delete-confirm-btn">Delete</button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function Modal({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-jet/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="relative w-full max-w-2xl bg-jet-elev1 border border-hairline max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-hairline px-5 py-3">
                    <div>
                        <div className="overline">Action</div>
                        <div className="heading-display text-lg">{title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <div><label className="overline block mb-2">{label}</label>{children}</div>;
}
